import { A11yModule } from '@angular/cdk/a11y';
import { ConnectedPosition, OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDividerModule } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { IntercomService } from '@core/providers/backend/intercom.service';
import { UserService } from '@core/providers/user/user.service';
import { LogoutService } from '@features/login/logout.service';
import { DefaultAvatarComponent } from '@shared/components/default-avatar/default-avatar.component';
import { ProfilePictureComponent } from '@shared/components/profile-picture/profile-picture.component';
import { CONSTANTS } from '@shared/constants';
import { type UserData } from '@shared/interfaces/login-object';
import { GlobalSearchService } from '../global-search/global-search.service';
import { BRAND_MARK_GROUP_KEY, navGroupIcon } from '../navigation/nav-group-icons';
import { type NavGroup } from '../navigation/nav-model.interface';
import { NavModelService } from '../navigation/nav-model.service';
import { ShellNavService } from '../shell-nav.service';

/** Grace period that lets the pointer cross the gap between the rail and an open pop-out. */
const POPOUT_CLOSE_DELAY_MS = 140;

/**
 * The pop-out extends out and down from the item, so its top edge lines up with the item's:
 * the pointer travels in one direction instead of doubling back past a centred panel.
 * Falls back to growing upwards, then to centring, when there isn't room below.
 */
const SUBMENU_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 12 },
  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12 },
  { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: 12 },
];

const PROFILE_POSITIONS: ConnectedPosition[] = [
  { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: 12 },
  { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: 12 },
];

const PROFILE_KEY = 'profile';

/** Intercom "getting started" article behind the profile menu's Help action. */
const HELP_ARTICLE_ID = '12817921';

/**
 * Apple keyboards label the modifier differently, and the hint is what teaches the shortcut.
 * Both forms spell out the chord with a "+" so it reads as two keys pressed together.
 */
function searchShortcutHint(): string {
  const isApple =
    typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.userAgent ?? '');

  return isApple ? '⌘ + K' : 'Ctrl + K';
}

/**
 * Hover-to-open only belongs on a pointer that can hover. A tap synthesises `mouseenter` and
 * then `click`, so on a touch screen the first tap opened the panel and closed it again, and
 * every menu needed two taps.
 */
function supportsHover(): boolean {
  return globalThis.matchMedia?.('(hover: hover)').matches ?? true;
}

@Component({
  selector: 'app-nav-rail',
  templateUrl: './nav-rail.component.html',
  styleUrls: ['./nav-rail.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    A11yModule,
    OverlayModule,
    RouterLink,
    MatIcon,
    MatDividerModule,
    MatTooltip,
    TranslatePipe,
    ProfilePictureComponent,
    DefaultAvatarComponent,
  ],
  host: {
    '(document:keydown.escape)': 'closePopout(true)',
    // Both chords, so the shortcut works on macOS and elsewhere.
    '(document:keydown.control.k)': 'toggleSearch($event)',
    '(document:keydown.meta.k)': 'toggleSearch($event)',
  },
})
export class NavRailComponent implements OnInit {
  private readonly navModel = inject(NavModelService);
  private readonly userService = inject(UserService);
  private readonly logoutService = inject(LogoutService);
  private readonly globalSearch = inject(GlobalSearchService);
  private readonly shellNav = inject(ShellNavService);
  private readonly destroyRef = inject(DestroyRef);

  readonly intercomService = inject(IntercomService);

  private closeTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly hoverCapable = supportsHover();

  readonly openPopoutKey = signal<string | null>(null);
  readonly userData = signal<UserData | null>(null);
  readonly userEmail = signal<string | null>('');

  /**
   * Whether the open panel should hold focus. Only a keyboard or click activation captures it —
   * a pop-out that appeared under the pointer must not pull focus off whatever the user was
   * typing in. The panel is rendered in an overlay at the end of the body, so without a trap
   * Tab left the rail entirely and the panel's links were unreachable.
   */
  private readonly popoutTakesFocus = signal(false);
  readonly popoutCapturesFocus = this.popoutTakesFocus.asReadonly();

  readonly submenuPositions = SUBMENU_POSITIONS;
  readonly profilePositions = PROFILE_POSITIONS;
  readonly profileKey = PROFILE_KEY;
  readonly routes = CONSTANTS.ROUTES;
  readonly searchShortcutHint = searchShortcutHint();

  readonly groups = this.navModel.groups;
  readonly activeGroupKey = this.navModel.activeGroupKey;
  readonly homeLink = this.navModel.homeLink;

  ngOnInit(): void {
    this.userService
      .getUserData$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(userData => {
        this.userData.set(userData);
        this.userEmail.set(this.userService.getUserEmail());
      });

    this.destroyRef.onDestroy(() => this.clearCloseTimer());
  }

  isGroupActive(group: NavGroup): boolean {
    return this.activeGroupKey() === group.key;
  }

  isActiveLink(link: string): boolean {
    return this.navModel.isActiveLink(link);
  }

  usesBrandMark(group: NavGroup): boolean {
    return group.key === BRAND_MARK_GROUP_KEY;
  }

  iconFor(group: NavGroup): string {
    return navGroupIcon(group.key, group.icon);
  }

  hasPopout(group: NavGroup): boolean {
    return group.sections.length > 0;
  }

  /**
   * Compact has no hover, so the item is the only way into the pop-out — and the group's link is
   * a landing page listing the destinations that pop-out already holds. A group that is a single
   * page has no pop-out, so it keeps its link.
   */
  opensPopoutOnTap(group: NavGroup): boolean {
    return this.shellNav.isCompact() && this.hasPopout(group);
  }

  openSearch(): void {
    this.closePopout(true);
    void this.globalSearch.open();
  }

  toggleSearch(event: Event): void {
    // The browser's own find-in-page shortcut must not also fire.
    event.preventDefault();
    this.closePopout(true);
    void this.globalSearch.toggle();
  }

  openOnHover(key: string): void {
    if (!this.hoverCapable) {
      return;
    }

    // Re-entering a panel that holds focus must not downgrade it to a hover-only one, or the
    // next `mouseleave` would close it and drop the keyboard user's focus to the body.
    if (this.openPopoutKey() === key && this.popoutTakesFocus()) {
      this.clearCloseTimer();
      return;
    }

    this.openPopout(key, false);
  }

  popoutId(key: string): string {
    return `fb-rail-popout-${key}`;
  }

  /**
   * Click, Enter and Space all arrive here. A keyboard activation reports `detail === 0`, and
   * only that moves focus into the panel: a pointer user is already where they want to be.
   */
  activate(key: string, event: MouseEvent): void {
    const fromKeyboard = event.detail === 0;

    if (this.openPopoutKey() === key && this.popoutTakesFocus() === fromKeyboard) {
      this.closePopout(true);
      return;
    }

    this.openPopout(key, fromKeyboard);
  }

  /** Arrow keys open a panel the same way Enter does — it extends to the right and down. */
  openFromKeyboard(key: string, event: Event): void {
    event.preventDefault();
    this.openPopout(key, true);
  }

  /**
   * A group that is a page of its own routes on click, and the pointer stays on the trigger, so
   * the panel it hovered open stays too — dismissing it under a hovering pointer reads as the
   * click having closed the menu, and users press the item again to get it back. A keyboard or
   * touch activation has no hover to hold it open, so those still dismiss.
   */
  followGroupLink(key: string, event: MouseEvent): void {
    const fromPointer = event.detail > 0 && this.hoverCapable;

    if (fromPointer) {
      this.openOnHover(key);
      return;
    }

    this.closePopout(true);
  }

  /**
   * The pointer left the trigger or the panel. A touch screen synthesises `mouseleave` on the
   * node tapped before this one and only dispatches the click a few hundred ms later, so acting
   * on it there tore the panel down before the tap could land on it. Focus inside the panel means
   * a keyboard user is working in it, and closing would drop their focus to the body, so the
   * pointer stops deciding then too.
   */
  closeOnPointerLeave(): void {
    if (!this.hoverCapable || this.popoutTakesFocus()) {
      return;
    }

    this.closePopout();
  }

  /**
   * The trigger lives outside the overlay, so its own click reaches the CDK's body-level capture
   * listener before the element's handler runs. Letting that dismiss the panel would override
   * whatever the trigger decides, so the trigger's own clicks are left to it.
   */
  closeOnOutsideClick(event: MouseEvent, trigger: HTMLElement): void {
    if (trigger.contains(event.target as Node)) {
      return;
    }

    this.closePopout(true);
  }

  /** Tabbing along the rail dismisses whatever hover left open behind it. */
  closeOtherPopout(key: string): void {
    const openKey = this.openPopoutKey();

    if (openKey !== null && openKey !== key) {
      this.closePopout(true);
    }
  }

  /** Immediate on Esc and on selection; delayed when the pointer merely leaves. */
  closePopout(immediately = false): void {
    this.clearCloseTimer();

    if (immediately) {
      this.dismissPopout();
      return;
    }

    this.closeTimer = setTimeout(() => {
      this.closeTimer = null;
      this.dismissPopout();
    }, POPOUT_CLOSE_DELAY_MS);
  }

  private openPopout(key: string, takesFocus: boolean): void {
    this.clearCloseTimer();

    // A key naming no group is the footer's own panel, which always opens.
    const group = this.groups().find(candidate => candidate.key === key);

    if (group && !this.hasPopout(group)) {
      this.dismissPopout();
      return;
    }

    this.popoutTakesFocus.set(takesFocus);
    this.openPopoutKey.set(key);
  }

  /** Tearing down the panel is what returns focus to the trigger: the focus trap restores it. */
  private dismissPopout(): void {
    this.popoutTakesFocus.set(false);
    this.openPopoutKey.set(null);
  }

  logOut(): void {
    this.closePopout(true);
    this.logoutService.logOut();
  }

  openHelp(event: MouseEvent): void {
    this.closePopout(true);
    this.intercomService.openArticleLink(event, HELP_ARTICLE_ID);
  }

  testId(prefix: string, value: string): string {
    const slug = value
      .toLowerCase()
      .replace(/[\s_/]+/g, '-')
      .replace(/[^a-z0-9-]/g, '')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');

    return `${prefix}-${slug || 'item'}`;
  }

  private clearCloseTimer(): void {
    if (this.closeTimer !== null) {
      clearTimeout(this.closeTimer);
      this.closeTimer = null;
    }
  }
}
